// FILE: useTranscriptSelection.ts
// Purpose: Track the text selected inside the transcript and where to anchor its actions.
// Layer: Web chat presentation
// Exports: useTranscriptSelection

import { useCallback, useEffect, useRef, useState } from "react";

import {
  resolveSelectionActionPosition,
  selectionActionDelayForClickCount,
  shouldHandleSelectionMouseUp,
} from "~/lib/selectionActions";
import { resolveMessageSelection, type MessageSelection } from "./messageSelectionActions";

export interface TranscriptSelectionState {
  selection: MessageSelection;
  position: { x: number; y: number };
}

// Reads the live selection and clips it to the message it started in. A drag that runs across
// several rows still quotes one message: the alternative is a quote whose text nobody ever wrote.
function readTranscriptSelection(container: HTMLElement): TranscriptSelectionState | null {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
    return null;
  }
  const anchorNode = selection.anchorNode;
  if (!anchorNode || !container.contains(anchorNode)) {
    return null;
  }
  const anchorElement =
    anchorNode.nodeType === Node.ELEMENT_NODE
      ? (anchorNode as Element)
      : (anchorNode.parentElement ?? null);
  const messageElement = anchorElement?.closest("[data-message-id]") ?? null;
  if (!messageElement) {
    return null;
  }

  const messageRange = document.createRange();
  messageRange.selectNodeContents(messageElement);
  const clipped = selection.getRangeAt(0).cloneRange();
  if (clipped.compareBoundaryPoints(Range.START_TO_START, messageRange) < 0) {
    clipped.setStart(messageRange.startContainer, messageRange.startOffset);
  }
  if (clipped.compareBoundaryPoints(Range.END_TO_END, messageRange) > 0) {
    clipped.setEnd(messageRange.endContainer, messageRange.endOffset);
  }

  const resolved = resolveMessageSelection({
    messageId: messageElement.getAttribute("data-message-id"),
    messageRole: messageElement.getAttribute("data-message-role"),
    // Text, not a Range: rows are virtualized, so the nodes this came from may be unmounted by
    // the time anyone acts on it.
    text: clipped.toString(),
    isCollapsed: selection.isCollapsed,
  });
  if (!resolved) {
    return null;
  }

  const selectionRect = clipped.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  return {
    selection: resolved,
    position: resolveSelectionActionPosition({
      bounds: {
        left: containerRect.left,
        top: containerRect.top,
        width: containerRect.width,
        height: containerRect.height,
      },
      selectionRect: { right: selectionRect.right, bottom: selectionRect.bottom },
      pointer: null,
    }),
  };
}

export function useTranscriptSelection(scrollElement: HTMLElement | null): {
  active: TranscriptSelectionState | null;
  dismiss: () => void;
} {
  const [active, setActive] = useState<TranscriptSelectionState | null>(null);
  const gestureActiveRef = useRef(false);
  const settleTimerRef = useRef<number | null>(null);

  const dismiss = useCallback(() => {
    if (settleTimerRef.current !== null) {
      window.clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
    setActive(null);
  }, []);

  useEffect(() => {
    const container = scrollElement;
    if (!container) {
      return;
    }

    const handleMouseUp = (event: MouseEvent) => {
      const shouldHandle = shouldHandleSelectionMouseUp(gestureActiveRef.current, event.button);
      gestureActiveRef.current = false;
      if (!shouldHandle) {
        return;
      }
      // A double or triple click settles its selection after the event, so reading it immediately
      // would see the previous, narrower range.
      settleTimerRef.current = window.setTimeout(() => {
        settleTimerRef.current = null;
        window.requestAnimationFrame(() => {
          setActive(readTranscriptSelection(container));
        });
      }, selectionActionDelayForClickCount(event.detail));
    };

    const handlePointerDown = (event: PointerEvent) => {
      dismiss();
      gestureActiveRef.current = event.button === 0;
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        dismiss();
      }
    };

    const handleSelectionChange = () => {
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed) {
        dismiss();
      }
    };

    // mouseup on window, not the container: a drag very often ends outside it.
    window.addEventListener("mouseup", handleMouseUp);
    window.addEventListener("keydown", handleKeyDown);
    document.addEventListener("selectionchange", handleSelectionChange);
    // The anchor is viewport-relative, so it goes stale the moment the transcript scrolls.
    container.addEventListener("scroll", dismiss, { passive: true });
    container.addEventListener("pointerdown", handlePointerDown);

    return () => {
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("selectionchange", handleSelectionChange);
      container.removeEventListener("scroll", dismiss);
      container.removeEventListener("pointerdown", handlePointerDown);
      gestureActiveRef.current = false;
      if (settleTimerRef.current !== null) {
        window.clearTimeout(settleTimerRef.current);
        settleTimerRef.current = null;
      }
    };
  }, [dismiss, scrollElement]);

  return { active, dismiss };
}
